import { selectState, Store } from 'dg-web-shared/lib/Flux.tsx';
import {
    Language,
    languageFromString,
    makeLocalizedText,
    Message,
} from 'dg-web-shared/lib/Localized.ts';
import { Conditional } from 'dg-web-shared/lib/ReactHelpers.tsx';
import * as SettingsState from '../../../common/state/SettingsState.ts';
import {
    FilterSummaryItem,
    FilterSummaryItemSubTitle,
    FilterSummaryItemTitle,
} from '../../../ui/filter/FilterSummary.tsx';
import { StringSingleSelectionSlideIn } from '../../../ui/slidein/SingleSelectionSlideIn.tsx';
import * as FilteredPermitsState from '../../state/FilteredPermitsState.ts';
import { OperatorAccount } from '../../../api/Http.ts';
import { SingleSelection } from '../../../ui/labeled-elements/SingleSelection.tsx';

export const TimeLimitFilterSelection = selectState(mapStateSlices, props => {
    const t = makeLocalizedText(props.language);

    return (
        <SingleSelection
            label={t(messages.filterTitle)}
            focused={false}
            selection={
                props.filter.timeLimit
                    ? t(messages[props.filter.timeLimit])
                    : null
            }
            onClick={() => {
                props.update(store =>
                    FilteredPermitsState.Filter.stateWrite(store, {
                        timeLimitSelectionVisible: true,
                    }),
                );
            }}
            onClear={
                props.filter.timeLimit
                    ? () => {
                          props.update(store =>
                              FilteredPermitsState.Filter.stateWrite(store, {
                                  timeLimit: null,
                              }),
                          );

                          props.update(FilteredPermitsState.List.reset);
                      }
                    : null
            }
        />
    );
});

export const TimeLimitFilterSummary = selectState(mapStateSlices, props => {
    const t = makeLocalizedText(props.language);

    return (
        <Conditional c={!!props.filter.timeLimit}>
            <FilterSummaryItem hasSeparator={true}>
                <FilterSummaryItemTitle title={t(messages.filterTitle)} />
                <FilterSummaryItemSubTitle
                    title={
                        '\u00A0' +
                        (props.filter.timeLimit
                            ? t(messages[props.filter.timeLimit])
                            : '-')
                    }
                />
            </FilterSummaryItem>
        </Conditional>
    );
});

export const TimeLimitFilterHalfSlideIn = selectState(mapStateSlices, props => {
    const language = languageFromString(props.language);
    const t = makeLocalizedText(language);

    return (
        <StringSingleSelectionSlideIn
            open={props.filter.timeLimitSelectionVisible}
            heading={t(messages.filterTitle)}
            selection={props.filter.timeLimit}
            options={filterOptions(language)}
            onLeftActionClick={() => {
                props.update(store =>
                    FilteredPermitsState.Filter.stateWrite(store, {
                        timeLimitSelectionVisible: false,
                    }),
                );
            }}
            onSelect={v => {
                props.update(store =>
                    FilteredPermitsState.Filter.stateWrite(store, {
                        timeLimit: v as OperatorAccount.Permits.TimeLimit,
                        timeLimitSelectionVisible: false,
                    }),
                );

                props.update(FilteredPermitsState.List.reset);
            }}
        />
    );
});

function filterOptions(
    language: Language,
): { id: string; displayText: string }[] {
    const t = makeLocalizedText(language);

    return Object.keys(OperatorAccount.Permits.TimeLimit).map((id: string) => ({
        id,
        displayText: t(messages[id as OperatorAccount.Permits.TimeLimit]),
    }));
}

function mapStateSlices(store: Store) {
    return {
        language: languageFromString(
            new SettingsState.StateSlice(store).state.language,
        ),
        filter: FilteredPermitsState.Filter.get(store),
    };
}

const messages: Messages = {
    filterTitle: {
        de: 'Kategorie',
        fr: 'Catégorie',
        it: 'Categoria',
        en: 'Category',
    },
    [OperatorAccount.Permits.TimeLimit.LIMITED_WITH_PAYMENT]: {
        de: 'Bewilligung mit Zahlungsabwicklung',
        fr: 'Autorisation avec gestion paiement',
        it: 'Autorizzazione con gestione pagamento',
        en: 'Limited permit with payment processing',
    },
    [OperatorAccount.Permits.TimeLimit.LIMITED_WITHOUT_PAYMENT]: {
        de: 'Bewilligung ohne Zahlungsabwicklung',
        fr: 'Autorisation sans gestion paiement',
        it: 'Autorizzazione senza gestione pagamento',
        en: 'Limited permit without payment processing',
    },
    [OperatorAccount.Permits.TimeLimit.UNLIMITED]: {
        de: 'Unbegrenzte Bewilligung',
        fr: 'Autorisation indéterminée',
        it: 'Autorizzazione indeterminata',
        en: 'Unlimited permit',
    },
};

interface Messages {
    filterTitle: Message;
    [OperatorAccount.Permits.TimeLimit.LIMITED_WITH_PAYMENT]: Message;
    [OperatorAccount.Permits.TimeLimit.LIMITED_WITHOUT_PAYMENT]: Message;
    [OperatorAccount.Permits.TimeLimit.UNLIMITED]: Message;
}
